import { AppConfig, UNKNOWN_CONFIG } from './app-config';

/**
 * This function is responsible for loading runtime configuration for the application.
 *
 * It is intended to be used in the `main.ts` file prior to Angular application bootstrapping.
 * Loading the runtime configuration via the `APP_INITIALIZER` was tried in many different
 * ways. It mostly worked, but we could never ensure that the `APP_INITIALIZER` completed before
 * the Okta provider factory executed, thus resulting in an misconfigured Okta library. We're loading
 * the config prior to app bootstrapping according to Okta recommendations described here:
 * [Loading configurations from external APIs](https://developer.okta.com/blog/2022/02/24/angular-async-config#loading-configurations-from-external-apis)
 *
 */
export const loadRemoteConfig = async (configUri = 'assets/config.json'): Promise<AppConfig> => {
    let remoteConfig: AppConfig;

    try {
        // we're using `fetch` because Angular's http client isn't available yet prior to bootstrapping.
        const response = await fetch(configUri);
        remoteConfig = (await response.json()) as AppConfig;
    } catch (error) {
        remoteConfig = UNKNOWN_CONFIG;
        // eslint-disable-next-line no-console -- runs pre-bootstrap; Angular DI/NGXLogger unavailable
        console.error('Unable to load runtime config file. Login and API features will not work.');
        // eslint-disable-next-line no-console -- runs pre-bootstrap; Angular DI/NGXLogger unavailable
        console.error(error);
    }
    return remoteConfig;
};
